// Pure helpers for the Gantt timeline. Window is computed around
// today + an offset (days) so the toolbar shifters do not need to
// know how the grid is laid out. Day/week/month zoom each set unit
// width and total unit count.
import type { GanttZoom } from "../../../../../../shared/types/production-schedule";
import { daysBetween, startOfLocalDay } from "./dates";

export interface GanttScale {
  zoom: GanttZoom;
  unitDays: number;
  unitWidth: number;
  totalUnits: number;
  startDay: Date;
  totalWidth: number;
}

export function makeScale(zoom: GanttZoom, offsetDays: number): GanttScale {
  const unitDays = zoom === "day" ? 1 : zoom === "week" ? 7 : 30;
  const unitWidth = zoom === "day" ? 40 : zoom === "week" ? 64 : 96;
  const totalUnits = zoom === "day" ? 42 : zoom === "week" ? 12 : 6;
  const today = startOfLocalDay();
  const startDay = new Date(today);
  startDay.setDate(
    today.getDate() + offsetDays - Math.floor((totalUnits * unitDays) / 4),
  );
  return {
    zoom,
    unitDays,
    unitWidth,
    totalUnits,
    startDay,
    totalWidth: totalUnits * unitWidth,
  };
}

export function unitAt(scale: GanttScale, idx: number): Date {
  const d = new Date(scale.startDay);
  d.setDate(scale.startDay.getDate() + idx * scale.unitDays);
  return d;
}

export function isTodayUnit(scale: GanttScale, idx: number): boolean {
  if (scale.zoom !== "day") return false;
  const d = unitAt(scale, idx);
  return d.toDateString() === startOfLocalDay().toDateString();
}

export function unitLabel(scale: GanttScale, idx: number): string {
  const d = unitAt(scale, idx);
  if (scale.zoom === "day")
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  if (scale.zoom === "week")
    // Use the week-starting date itself; the mockup's `W{N}` label
    // didn't align to ISO weeks and read as "two W2s in adjacent
    // months" once the year tipped.
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  return d.toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
}

// Pixel offset from the scale start to the given ISO date.
export function pxAt(scale: GanttScale, iso: string): number {
  return (daysBetween(scale.startDay, new Date(iso)) / scale.unitDays) * scale.unitWidth;
}

// Pixel width covering [startIso, endIso] inclusive.
export function pxWidth(scale: GanttScale, startIso: string, endIso: string): number {
  const w = (daysBetween(new Date(startIso), new Date(endIso)) / scale.unitDays) * scale.unitWidth;
  return Math.max(20, w);
}

// Clipped bar bounds for a [startIso, endIso] range against the
// visible scale window. Returns null when the bar falls entirely
// outside the window (so callers can skip rendering). Otherwise the
// returned left + width stay within [0, scale.totalWidth] so bars
// never bleed into the row label column on the left or past the
// timeline on the right.
export function barBounds(
  scale: GanttScale,
  startIso: string,
  endIso: string,
  minWidth = 0,
): { left: number; width: number; clippedLeft: boolean; clippedRight: boolean } | null {
  const rawLeft = pxAt(scale, startIso);
  const rawWidth = Math.max(minWidth, pxWidth(scale, startIso, endIso));
  const rawRight = rawLeft + rawWidth;
  if (rawRight <= 0 || rawLeft >= scale.totalWidth) return null;
  const left = Math.max(0, rawLeft);
  const right = Math.min(scale.totalWidth, rawRight);
  const width = Math.max(0, right - left);
  if (width <= 0) return null;
  return {
    left,
    width,
    clippedLeft: rawLeft < 0,
    clippedRight: rawRight > scale.totalWidth,
  };
}

export function todayPx(scale: GanttScale): number | null {
  const today = startOfLocalDay();
  const px = (daysBetween(scale.startDay, today) / scale.unitDays) * scale.unitWidth;
  if (px < 0 || px > scale.totalWidth) return null;
  return px;
}
